import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {
  type InfiniteData,
  type QueryKey,
  useSuspenseInfiniteQuery,
  type UseSuspenseInfiniteQueryResult,
} from '@github-ui/react-query'
import {reactFetch} from '@github-ui/verified-fetch'
import type {AnnotationsPayload} from '../payloads/annotations'
import {parseLinkHeader} from '../../utils/parse-link-header'
import {useMarkersActions} from '../../stores/selectors'

export function annotationsQueryKey(pathName: string): QueryKey {
  return [PageData.annotations, pathName]
}

export type AnnotationsPage = {
  data: AnnotationsPayload
  nextUrl: string | null
}

export function annotationsApiUrl(pathName: string, endCommitOid: string): string {
  return `${pathName}/page_data/${PageData.annotations}?end_commit_oid=${encodeURIComponent(endCommitOid)}`
}

/**
 * Hook for fetching annotations with pagination support.
 * Returns the infinite query result directly, allowing consumers to manage pagination.
 *
 * The API will only return a link to the next page if there are more pages to fetch.
 * The end_commit_oid parameter anchors pagination to a specific commit SHA so pages
 * remain consistent even if the PR head moves between requests.
 *
 * When initialAnnotations are provided, they are used as the first page to avoid
 * re-fetching data that was already loaded in the initial payload.
 *
 * @param pathName - The base path for the API request
 * @param endCommitOid - The head SHA to anchor pagination to
 * @param initialAnnotations - Pre-loaded annotations to use as the first page
 * @param cursor - Cursor for the next page from the initial payload
 * @param hasMorePages - Whether the initial payload indicated more pages exist
 * @returns The infinite query result with pagination controls
 */
export function useInfiniteAnnotationsPageData({
  pathName,
  endCommitOid,
  initialAnnotations = [],
  cursor,
  hasMorePages = false,
}: {
  pathName: string
  endCommitOid: string
  initialAnnotations?: AnnotationsPayload
  cursor?: string
  hasMorePages?: boolean
}): UseSuspenseInfiniteQueryResult<InfiniteData<AnnotationsPage>> {
  const {updateAnnotations} = useMarkersActions()

  // Build the next page URL from the cursor only if there are more pages to fetch.
  // Include page=2 only when initial annotations were provided (they represent page 1).
  // When lazy loading (no cursor, no initial annotations), use the base API URL to fetch from scratch.
  const baseApiUrl = annotationsApiUrl(pathName, endCommitOid)
  let nextPageUrl: string | null = null
  if (hasMorePages) {
    if (cursor) {
      const baseNextUrl = `${baseApiUrl}&after=${encodeURIComponent(cursor)}`
      nextPageUrl = initialAnnotations.length > 0 ? `${baseNextUrl}&page=2` : baseNextUrl
    } else if (initialAnnotations.length === 0) {
      // Lazy loading: no cursor and no initial data — fetch from the beginning
      nextPageUrl = baseApiUrl
    }
  }

  return useSuspenseInfiniteQuery<AnnotationsPage, Error, InfiniteData<AnnotationsPage>, QueryKey, string>({
    queryKey: annotationsQueryKey(pathName),
    initialPageParam: baseApiUrl,
    initialData: {
      pages: [{data: initialAnnotations, nextUrl: nextPageUrl}],
      pageParams: [baseApiUrl],
    },
    queryFn: async ({pageParam}: {pageParam: string}): Promise<AnnotationsPage> => {
      const result = await reactFetch(pageParam)
      if (!result.ok) throw new Error(`Failed to fetch annotations from ${pageParam}: HTTP ${result.status}`)

      const json = await result.json()
      const linkHeader = result.headers.get('Link')
      const nextUrl = parseLinkHeader(linkHeader, 'next')
      const data = (json as AnnotationsPayload).map(annotation => ({
        ...annotation,
        hideUntilLoadedAndInView: true,
      }))

      updateAnnotations(data)

      return {data, nextUrl}
    },
    getNextPageParam: (lastPage: AnnotationsPage) => {
      return lastPage.nextUrl ?? undefined
    },
    staleTime: Infinity,
  })
}
